import { api } from './api';

export interface ActivityFeedItem {
  id: string;
  timestamp: string;
  relativeTime: string;
  userId?: string;
  userName?: string;
  userAvatarUrl?: string;
  userInitials?: string;
  action: string;
  actionIcon: string;
  actionColor: string;
  description: string;
  entityType: string;
  entityId?: string;
  entityName?: string;
  entityUrl?: string;
  details?: string;
}

export interface ActivityFeedGroup {
  groupTitle: string;
  groupDate: string;
  items: ActivityFeedItem[];
}

export interface ActivityFeedResponse {
  groups: ActivityFeedGroup[];
  items: ActivityFeedItem[];
  totalCount: number;
  hasMore: boolean;
  oldestTimestamp?: string;
}

export interface ActivityFeedFilter {
  userId?: string;
  entityTypes?: string[];
  actions?: string[];
  entityId?: string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
  groupByDate?: boolean;
}

export interface DailyActivityCount {
  date: string;
  dayName: string;
  count: number;
}

export interface HourlyActivityCount {
  hour: number;
  count: number;
}

export interface UserActivitySummary {
  userId: string;
  userName: string;
  avatarUrl?: string;
  activityCount: number;
  lastActivity: string;
}

export interface ActivityStats {
  todayCount: number;
  thisWeekCount: number;
  thisMonthCount: number;
  dailyActivity: DailyActivityCount[];
  hourlyActivity: HourlyActivityCount[];
  topUsers: UserActivitySummary[];
}

// Get activity feed with optional filtering
export const getActivityFeed = async (filter?: ActivityFeedFilter): Promise<ActivityFeedResponse> => {
  const params = new URLSearchParams();

  if (filter?.userId) params.append('userId', filter.userId);
  if (filter?.entityTypes?.length) params.append('entityTypes', filter.entityTypes.join(','));
  if (filter?.actions?.length) params.append('actions', filter.actions.join(','));
  if (filter?.entityId) params.append('entityId', filter.entityId);
  if (filter?.since) params.append('since', filter.since);
  if (filter?.until) params.append('until', filter.until);
  if (filter?.limit) params.append('limit', String(filter.limit));
  if (filter?.offset) params.append('offset', String(filter.offset));
  if (filter?.groupByDate !== undefined) params.append('groupByDate', String(filter.groupByDate));

  const response = await api.get<ActivityFeedResponse>('/api/activityfeed', { params });
  return response.data;
};

// Get recent activities for dashboard widget
export const getRecentActivities = async (limit = 10): Promise<ActivityFeedItem[]> => {
  const response = await api.get<ActivityFeedItem[]>('/api/activityfeed/recent', {
    params: { limit },
  });
  return response.data;
};

// Get activities for a specific entity
export const getEntityActivities = async (
  entityType: string,
  entityId: string
): Promise<ActivityFeedItem[]> => {
  const response = await api.get<ActivityFeedItem[]>(
    `/api/activityfeed/entity/${entityType}/${entityId}`
  );
  return response.data;
};

// Get activities for a specific user
export const getUserActivities = async (
  userId: string,
  limit = 50
): Promise<ActivityFeedItem[]> => {
  const response = await api.get<ActivityFeedItem[]>(`/api/activityfeed/user/${userId}`, {
    params: { limit },
  });
  return response.data;
};

// Get activity statistics
export const getActivityStats = async (days = 7): Promise<ActivityStats> => {
  const response = await api.get<ActivityStats>('/api/activityfeed/stats', {
    params: { days },
  });
  return response.data;
};

// Get activities since a timestamp (for polling)
export const getActivitiesSince = async (since: string): Promise<ActivityFeedItem[]> => {
  const response = await api.get<ActivityFeedItem[]>('/api/activityfeed/since', {
    params: { since },
  });
  return response.data;
};

// Helper function to get icon component name
export const getActionIconName = (action: string): string => {
  const iconMap: Record<string, string> = {
    'Create': 'PlusCircle',
    'Update': 'Edit2',
    'Delete': 'Trash2',
    'StatusChange': 'RefreshCw',
    'Assign': 'UserPlus',
    'Unassign': 'UserMinus',
    'Comment': 'MessageCircle',
    'Login': 'LogIn',
    'Logout': 'LogOut',
    'LoginFailed': 'AlertCircle',
    'Upload': 'Upload',
    'Download': 'Download',
    'Export': 'FileText',
    'View': 'Eye',
    'PasswordChanged': 'Key',
  };
  return iconMap[action] || 'Activity';
};

// Helper function to get action color
export const getActionColor = (action: string): string => {
  const colorMap: Record<string, string> = {
    'Create': 'text-green-600',
    'Update': 'text-[color:var(--dg-brand)]',
    'Delete': 'text-red-600',
    'StatusChange': 'text-purple-600',
    'Assign': 'text-indigo-600',
    'Unassign': 'text-orange-600',
    'Comment': 'text-[color:var(--dg-brand)]',
    'Login': 'text-green-600',
    'Logout': 'text-gray-600',
    'LoginFailed': 'text-red-600',
    'Upload': 'text-[color:var(--dg-brand)]',
    'Download': 'text-gray-600',
    'Export': 'text-gray-600',
    'View': 'text-gray-600',
    'PasswordChanged': 'text-yellow-600',
  };
  return colorMap[action] || 'text-gray-600';
};

// Helper function to get action background color
export const getActionBgColor = (action: string): string => {
  const colorMap: Record<string, string> = {
    'Create': 'bg-green-100',
    'Update': 'bg-[color:var(--dg-brand-soft)]',
    'Delete': 'bg-red-100',
    'StatusChange': 'bg-purple-100',
    'Assign': 'bg-indigo-100',
    'Unassign': 'bg-orange-100',
    'Comment': 'bg-[color:var(--dg-brand-soft)]',
    'Login': 'bg-green-100',
    'Logout': 'bg-gray-100',
    'LoginFailed': 'bg-red-100',
    'Upload': 'bg-[color:var(--dg-brand-soft)]',
    'Download': 'bg-gray-100',
    'Export': 'bg-gray-100',
    'View': 'bg-gray-100',
    'PasswordChanged': 'bg-yellow-100',
  };
  return colorMap[action] || 'bg-gray-100';
};
